import { Float32BufferAttribute, type BufferGeometry } from 'three';
import { scaleColor, type LinearRGB } from '../../core/color';
import { createMeshBuilder, type VertexStyle } from '../mesh/meshBuilder';
import { SURFACE, type SurfaceKind } from '../mesh/attributes';
import { SHIP_TEMPLATES, type ShipType, type BoatAppearance } from './shipTemplates';
import { buildHull } from './hull';
import { buildBoatBody, isOpenBoat } from './openBoat';
import { formedTemplate, isFormed } from './shipForm';
import { buildFormedHull, type HullPalette, type ShipTier } from './shipHull';
import { buildStandingRig } from './shipMasts';
import { buildRailFlags } from './railFlags';
import { withClothWind, type ClothSpan } from '../mesh/clothWind';
import { runningRigGeometry } from './shipYards';
import { rigStylesFor } from './rigParts';
import { buildRigging, pennantObstacles, staffTops } from './rigging';
import {
  buildPennant,
  pennantLengths,
  withPainterHoists,
  PENNANT_LENGTH_PER_SHIP_LENGTH,
} from './pennant';
import type { ShipRig } from './shipRig';
import type { PennantColour } from './pennantColours';
import { PATTERN_CODE_FLOOR } from '../../render/patterns/patternIds';
import { smokeGeometry } from './smokeGeometry';
import { joinGeometries } from './geometryJoin';
import type { SurfaceMesh } from './surfaceNet';
import { SHIP_PAINTER_SCALE_H, type PainterAnchor } from './shipPlacement';

export interface ShipColors {
  readonly hull: LinearRGB;
  /** A formed hull's wales, rails and open ports (hullParts.ts HullPalette). */
  readonly wale: LinearRGB;
  readonly rail: LinearRGB;
  readonly port: LinearRGB;
  /** An open boat's hull: seen from above, into its hold, in the shade of its sides. */
  readonly openHull: LinearRGB;
  readonly rigging: LinearRGB;
  readonly sail: LinearRGB;
  /** The colours the painted pennants fly (pennantColours.ts). */
  readonly pennants: Readonly<Record<PennantColour, LinearRGB>>;
  readonly smoke: LinearRGB;
}

/** Working timber stays within the painting's brown palette; these are material readings,
 * not a claim that the scan identifies a coating on any particular boat. */
export function boatColors(colors: ShipColors, finish: BoatAppearance['finish']): ShipColors {
  const shade = finish === 'tarred' ? 0.72 : finish === 'weathered' ? 1.18 : 1.4;
  return {
    ...colors,
    hull: scaleColor(colors.hull, shade),
    wale: scaleColor(colors.wale, finish === 'weathered' ? 0.78 : finish === 'oak' ? 1.12 : 0.9),
    openHull: scaleColor(colors.openHull, finish === 'oak' ? 1.2 : 1),
  };
}

function styleOf(
  color: LinearRGB,
  surface: SurfaceKind,
  edgeId: number,
  seed: number
): VertexStyle {
  return { color, surface, edgeId, seed, strokeAngle: 0, extent: [0, 0] };
}

/* A boat moored stern-to grows in the painter view about its stern, out over the water rather
   than into the bank: before the painter scale, every vertex moves by the shift that scale would
   give the stern, undone. */
function grownFromStern(geometry: BufferGeometry, stern: number): BufferGeometry {
  const position = geometry.getAttribute('position');
  const current = geometry.getAttribute('aAxisOffset');
  const offsets = current ? Float32Array.from(current.array) : new Float32Array(position.count * 3);
  const shift = -((SHIP_PAINTER_SCALE_H - 1) / SHIP_PAINTER_SCALE_H) * stern;
  for (let i = 0; i < position.count; i++) offsets[i * 3] = offsets[i * 3]! + shift;
  geometry.setAttribute('aAxisOffset', new Float32BufferAttribute(offsets, 3));
  return geometry;
}

/**
 * The hull, rig and pennants of a ship hang on the ship that carries them only through the outline
 * id and stroke seed at their vertices: a stage builds each once, for all the ships that share it,
 * and every ship takes a copy with its own. The parts are built in the fleet's colours with id and
 * seed nought. A boat's hull is one part; a formed ship's hull and standing rig are its body, one
 * for each distance tier (docs/plans/ships.md 4), and an open boat's detail within 250 m is its
 * body for the mid and near tiers (ships.md 9).
 */
export interface ShipParts {
  readonly colors: ShipColors;
  hull(type: ShipType): BufferGeometry;
  body(type: ShipType, tier: ShipTier): BufferGeometry;
  rigging(type: ShipType, rig: ShipRig): BufferGeometry;
  pennants(type: ShipType, rig: ShipRig, colour: PennantColour): BufferGeometry;
}

/**
 * The vertices a builder makes room for, for each part, where it would make room for 4,096: a
 * boat's hull has up to 478, a formed ship's body up to 2,386, 13,028 and 18,100 in the far, mid
 * and near tiers (its subdivided waving rail flags included), a boat's sprit rig up to 558 and the pennants of a ship up to 762
 * (a red pennant's pale bands included).
 * A formed ship's yards and sails are built piece by piece (shipYards.ts PIECE_VERTEX_ROOM). A
 * builder that runs out of room copies all it holds into room twice as large, and room to spare is
 * cleared all the same. Open boat shells need room for their held-open far hulls, and the mid
 * and near tiers also carry the small cleats and knees.
 */
export const PART_VERTEX_ROOM = {
  hull: 1280,
  body: { far: 2560, mid: 14336, near: 18432 },
  boatBody: { far: 1280, mid: 1536, near: 3072 },
  rigging: 640,
  pennants: 1024,
} as const;

function hullGeometry(type: ShipType, colors: ShipColors): BufferGeometry {
  if (isOpenBoat(SHIP_TEMPLATES[type])) return boatBodyGeometry(type, 'far', colors);
  const template = SHIP_TEMPLATES[type];
  const builder = createMeshBuilder(PART_VERTEX_ROOM.hull);
  buildHull(builder, template, template.masts.length === 0 ? colors.openHull : colors.hull, 0, 0);
  return builder.build();
}

function hullPaletteOf(colors: ShipColors): HullPalette {
  return {
    hull: colors.hull,
    wale: colors.wale,
    rail: colors.rail,
    port: colors.port,
    rope: colors.rigging,
  };
}

/* An open boat's hold remains in shade inside its lighter planked shell. */
function boatBodyGeometry(type: ShipType, tier: ShipTier, colors: ShipColors): BufferGeometry {
  const builder = createMeshBuilder(PART_VERTEX_ROOM.boatBody[tier]);
  const palette = { ...hullPaletteOf(colors), port: colors.openHull };
  buildBoatBody(builder, SHIP_TEMPLATES[type], tier, palette, 0, 0);
  return builder.build();
}

function bodyGeometry(type: ShipType, tier: ShipTier, colors: ShipColors): BufferGeometry {
  if (isOpenBoat(SHIP_TEMPLATES[type])) return boatBodyGeometry(type, tier, colors);
  const template = formedTemplate(SHIP_TEMPLATES[type]);
  const builder = createMeshBuilder(PART_VERTEX_ROOM.body[tier]);
  buildFormedHull(builder, template, tier, hullPaletteOf(colors), 0, 0);
  buildStandingRig(builder, template, tier, rigStylesFor(colors.rigging, colors.sail, 0, 0));
  let spans: readonly ClothSpan[] = [];
  if (tier !== 'far') {
    const flags = { field: colors.rail, saltire: colors.pennants.red, staff: colors.hull };
    spans = buildRailFlags(builder, template, flags);
  }
  return withClothWind(builder.build(), spans);
}

/* A boat's sprit rig whole, or a formed ship's yards and sails as the painter view draws them. */
function riggingGeometry(type: ShipType, rig: ShipRig, colors: ShipColors): BufferGeometry {
  const template = SHIP_TEMPLATES[type];
  const { sailState, brace } = rig;
  if (isFormed(template)) {
    const styles = rigStylesFor(colors.rigging, colors.sail, 0, 0);
    return runningRigGeometry(template, sailState, brace, styles);
  }
  const builder = createMeshBuilder(PART_VERTEX_ROOM.rigging);
  buildRigging(builder, template, sailState, colors.rigging, colors.sail, 0, 0, brace);
  return builder.build();
}

/* A pennant on every masthead, streaming with the wind, raised in the painter view. */
function pennantsGeometry(
  type: ShipType,
  rig: ShipRig,
  colour: PennantColour,
  colors: ShipColors
): BufferGeometry {
  const template = SHIP_TEMPLATES[type];
  const builder = createMeshBuilder(PART_VERTEX_ROOM.pennants);
  const flag = styleOf(colors.pennants[colour], SURFACE.cloth, 0, 0);
  /* The painted red streamers are red and white (pennant.ts PENNANT_BANDS). */
  const bands = colour === 'red' ? styleOf(colors.rail, SURFACE.cloth, 0, 0) : undefined;
  const tops = staffTops(template);
  const full = PENNANT_LENGTH_PER_SHIP_LENGTH * template.length;
  const lengths = pennantLengths(tops, rig.downwind, full, pennantObstacles(template));
  const spans = tops.map((staffTop, i) =>
    buildPennant(builder, flag, { staffTop, length: lengths[i]!, downwind: rig.downwind }, bands)
  );
  const windSpans = spans.map((span, i): ClothSpan => ({
    ...span,
    end: Math.max(span.end, ...span.bands.map((band) => band.end)),
    anchor: tops[i]!,
    along: rig.downwind,
    normal: [-rig.downwind[2] * 0.94, 0.34, rig.downwind[0] * 0.94],
    length: lengths[i]!,
    amplitude: Math.min(0.9, lengths[i]! * 0.08),
    phase: i * 2.399,
  }));
  return withClothWind(withPainterHoists(builder.build(), spans), windSpans);
}

function remembered<K, V>(built: Map<K, V>, key: K, build: () => V): V {
  const known = built.get(key);
  if (known !== undefined) return known;
  const part = build();
  built.set(key, part);
  return part;
}

/** The parts of the ships of one stage, each built the first time a ship asks for it. */
export function createShipParts(colors: ShipColors): ShipParts {
  const hulls = new Map<ShipType, BufferGeometry>();
  const bodies = new Map<string, BufferGeometry>();
  const riggings = new Map<string, BufferGeometry>();
  const pennants = new Map<string, BufferGeometry>();
  return {
    colors,
    hull: (type) => remembered(hulls, type, () => hullGeometry(type, colors)),
    body: (type, tier) =>
      remembered(bodies, `${type} ${tier}`, () => bodyGeometry(type, tier, colors)),
    rigging: (type, rig) =>
      remembered(riggings, [type, rig.sailState, rig.brace].join(' '), () =>
        riggingGeometry(type, rig, colors)
      ),
    pennants: (type, rig, colour) =>
      remembered(pennants, [type, ...rig.downwind, colour].join(' '), () =>
        pennantsGeometry(type, rig, colour, colors)
      ),
  };
}

/* The ship's own outline id at every vertex of a geometry made for it, and its stroke seed at
   every vertex that draws no pattern: aMeta.z carries a patterned part's preset (STYLE 15.1). */
function owned(geometry: BufferGeometry, edgeId: number, seed: number): BufferGeometry {
  const meta = geometry.getAttribute('aMeta').array;
  for (let i = 0; i < meta.length; i += 4) {
    meta[i + 1] = edgeId;
    if (meta[i + 2]! < PATTERN_CODE_FLOOR) meta[i + 2] = seed;
  }
  return geometry;
}

/**
 * A formed ship's body in its own frame (bow at -X) for one tier: its hull and standing rig,
 * with its own outline id and stroke seed. The painter view grows it about its anchor.
 */
export function buildBodyGeometry(
  type: ShipType,
  tier: ShipTier,
  edgeId: number,
  seed: number,
  anchor: PainterAnchor,
  parts: ShipParts
): BufferGeometry {
  const geometry = owned(joinGeometries([parts.body(type, tier)]), edgeId, seed);
  return anchor === 'stern' ? grownFromStern(geometry, SHIP_TEMPLATES[type].length / 2) : geometry;
}

/** Where a ship geometry's salute begins in its index: its smoke, drawn last. */
export const SMOKE_FROM = 'smokeFrom';

/**
 * One ship in its own frame (bow at -X) as its group draws it: a boat's hull, the rig with its
 * yards braced for its sails, a pennant in its colour on every masthead streaming with the wind, and the clouds
 * of smoke of its salute, laid in its frame at the ends that fire. A formed ship's hull and
 * standing rig are in its body instead. The painter view grows it about its anchor. The hull and
 * rig come from the stage's parts, when it has them.
 */
export function buildShipGeometry(
  type: ShipType,
  rig: ShipRig,
  pennant: PennantColour,
  colors: ShipColors,
  edgeId: number,
  seed: number,
  clouds: readonly SurfaceMesh[],
  anchor: PainterAnchor = 'origin',
  parts: ShipParts = createShipParts(colors)
): BufferGeometry {
  const template = SHIP_TEMPLATES[type];
  const hull = isFormed(template) ? [] : [parts.hull(type)];
  const rigged =
    template.masts.length > 0 ? [parts.rigging(type, rig), parts.pennants(type, rig, pennant)] : [];
  const smoke = styleOf(colors.smoke, SURFACE.smoke, edgeId, seed);
  const salute = clouds.length > 0 ? [smokeGeometry(clouds, smoke)] : [];
  const geometry = owned(joinGeometries([...hull, ...rigged, ...salute]), edgeId, seed);
  geometry.userData[SMOKE_FROM] = [...hull, ...rigged].reduce(
    (sum, part) => sum + (part.getIndex()?.count ?? 0),
    0
  );
  return anchor === 'stern' ? grownFromStern(geometry, SHIP_TEMPLATES[type].length / 2) : geometry;
}
